<template>
  <div class="header">
    <a-button type="link" class="back-button" @click="returnRoute" style="padding: 0">
      <ArrowLeftOutlined />
      Volver
    </a-button>
    <h3>Conector de API</h3>
    <div>
      <a-tooltip>
        <template #title v-if="disabledSave">
          {{
            !isEdit ?
            'Completa los campos requeridos para integrar el conector de API.' :
            'Completa los campos requeridos para guardar cambios del conector de API.'
          }}
        </template>
        <a-button
          type="primary"
          @click="handleSubmit"
          :loading="loading"
          :disabled="disabledSave"
        >
          {{ isEdit? "Guardar Cambios" : "Integrar"}}
        </a-button>
      </a-tooltip>
    </div>
  </div>
  <CustomScrollbar height="calc(100vh - 111px)" auto>
    <div class="container">
      <div class="form_container">
        <a-form layout="vertical" ref="formRef" :model="formState" :rules="rules" required>
          <h3 class="title">Información general</h3>
          <div class="section">
            <a-form-item
              label="Nombre del conector de API"
              name="name"
              :extra="'El nombre del conector debe ser único por conector.'"
            >
              <a-input v-model:value="formState.name" placeholder="Document" maxlength="100" />
            </a-form-item>
            <a-form-item label="Descripción" name="description">
              <a-textarea
                v-model:value="formState.description"
                :rows="4"
                placeholder="Describe el objetivo del conector de API"
                maxlength="1000"
                show-count
              />
            </a-form-item>
            <a-form-item name="type_request">
              <template #label>
                Tipo de petición
                <a-tooltip>
                  <template #title>
                    <div>Define el tipo de acción que realizará tu solicitud hacia la API.</div>
                  </template>
                  <InfoCircleOutlined style="margin-left: 5px" />
                </a-tooltip>
              </template>
              <a-select
                v-model:value="formState.type_request"
              >
                <a-select-option v-for="type in requestTypes" :key="type.value">
                  <a-popover
                    trigger="hover"
                    placement="rightBottom"
                    :zIndex="4000"
                    width="50px"
                  >
                    <template #content>
                      {{ DescriptionRequestType[type.value] }}
                    </template>
                    <div style="width: 50%">
                      {{ type.label }}
                    </div>
                  </a-popover>
                </a-select-option>
              </a-select>
            </a-form-item>
            <a-form-item label="URL de API Server" name="url">
              <a-input v-model:value="formState.url" placeholder="https://..." />
            </a-form-item>
            
            <a-card style="margin-top: 15px" size="small">
              <h4 class="subtitle">Datos de autenticación</h4>
              <a-row :gutter="16">
                <a-col :span="24">
                  <a-form-item name="type_authentication">
                    <template #label>
                      Tipo de autenticación
                      <a-tooltip>
                        <template #title>
                          <div>Selecciona el método que el conector usará para autenticarse con el API.</div>
                        </template>
                        <InfoCircleOutlined style="margin-left: 5px" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model:value="formState.type_authentication"
                      placeholder="Seleccionar tipo"
                      @change="clearFieldsAuthentication"
                      :getPopupContainer="(triggerNode) => triggerNode.parentNode"
                    >
                      <a-select-option v-for="type in authenticationTypes" :key="type.value">
                        <a-popover
                          trigger="hover"
                          overlayClassName="custom-pop"
                          placement="rightBottom"
                          :zIndex="4000"
                        >
                          <template #content>
                            <p>{{ DescriptionAuthenticationType[type.value] }}</p>
                            <img width="440px" :src="ImageAuthenticationType[type.value]">
                          </template>
                          <div style="width: 50%">
                            {{ type.label }}
                          </div>
                        </a-popover>
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
              </a-row>
              <a-row :gutter="16" v-if="formState.type_authentication == AuthenticationType.APIKey">
                <a-col :span="24">
                  <a-form-item label="API Key de autenticación" name="api_key">
                    <a-input-password v-model:value="formState.api_key" placeholder="Escribir API Key" />
                  </a-form-item>
                </a-col>
              </a-row>
              <a-row :gutter="16" v-else-if="formState.type_authentication == AuthenticationType.Token">
                <a-col :span="24">
                  <a-form-item label="Token de autenticación" name="url_token">
                    <a-input v-model:value="formState.url_token" placeholder="Ingresar URL" />
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item label="Nombre de usuario" name="username_token">
                    <a-input v-model:value="formState.username_token" placeholder="Escribir usuario" autocomplete="off" />
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item label="Contraseña" name="password_token">
                    <a-input-password v-model:value="formState.password_token" placeholder="Escribir contraseña" autocomplete="off" />
                  </a-form-item>
                </a-col>
              </a-row>
              <a-row :gutter="16" v-else-if="formState.type_authentication == AuthenticationType.BasicAuth">
                <a-col :span="12">
                  <a-form-item label="Nombre de usuario" name="username">
                    <a-input v-model:value="formState.username" placeholder="Escribir usuario" autocomplete="off" />
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item label="Contraseña" name="password">
                    <a-input-password v-model:value="formState.password" placeholder="Escribir contraseña" autocomplete="off" />
                  </a-form-item>
                </a-col>
              </a-row>
            </a-card>
          </div>

          <h3 class="title">Configuración</h3>
          <span>Configura los datos que tu conector enviará al API y cómo interpretará la respuesta para continuar los flujos del IVR Operador Automático y/o chatbots de WhatsApp.</span>

          <div class="section">
            <a-row :gutter="16">
              <a-col :span="16">
                <a-form-item name="ivr_param_name">
                  <template #label>
                    Nombre del parámetro a capturar
                    <a-tooltip>
                      <template #title>
                        Es el dato que el IVR y/o Chatbot de WhatsApp necesita recoger o recibir del usuario para poder armar la petición hacia la API.
                      </template>
                      <InfoCircleOutlined style="margin-left: 5px" />
                    </a-tooltip>
                  </template>
                  <a-input v-model:value="formState.ivr_param_name" placeholder="Escribir el nombre" />
                </a-form-item>
              </a-col>
              <a-col :span="8">
                <a-form-item name="ivr_param_name">
                  <template #label>
                    Tipo de envío
                    <a-tooltip>
                      <template #title>
                        Indica desde dónde se enviarán los datos al  API: en la URL como parámetros (params) o en el cuerpo de la solicitud (body).
                      </template>
                      <InfoCircleOutlined style="margin-left: 5px" />
                    </a-tooltip>
                  </template>
                  <a-select
                    v-model:value="formState.type_send"
                    :options="sendTypes"
                    placeholder="Seleccionar una opción"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-card size="small">
              <div class="section_header">
                <h4 class="subtitle">
                  Propiedades estándar para enviar al API
                  <a-tooltip>
                    <template #title>
                      Parámetros comunes que se envían y requiere el API para filtrar, ordenar o autenticar las peticiones.
                    </template>
                    <InfoCircleOutlined style="margin: 0 3px" />
                  </a-tooltip>
                  <span class="subtitle--extra">(opcional)</span>
                </h4>
                <span class="section_header--detail">
                  Máximo 5 propiedades
                </span>
              </div>
              <StandardPropertiesCard
                v-if="!loadingFetch"
                v-model="formState.standard_properties"
              />
            </a-card>

            <a-card style="margin-top: 15px" size="small">
              <h4 class="subtitle">Respuesta del API</h4>
              <a-row :gutter="16">
                <a-col :span="16">
                  <a-form-item name="api_return_field">
                    <template #label>
                      Nombre del campo a devolver del API
                      <a-popover overlayClassName="custom-pop">
                        <template #content>
                          <p>
                            Utiliza una clave única proporcionada por el servicio para autorizar las peticiones de la API.
                          </p>
                          <img width="440px" :src="imageResponseApi" />
                        </template>
                        <InfoCircleOutlined style="margin-left: 5px" />
                      </a-popover>
                    </template>
                    <a-input v-model:value="formState.api_return_field" placeholder="Escribir el nombre del campo" />
                  </a-form-item>
                </a-col>
                <a-col :span="8">
                  <a-form-item name="data_type">
                    <template #label>
                      Tipo de dato:
                      <a-tooltip title="Especifica el tipo de valor que devuelve el campo de la API">
                        <InfoCircleOutlined style="margin-left: 5px" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model:value="formState.data_type"
                      placeholder="Escribir dato"
                      @change="changeResponseType"
                    >
                      <a-select-option
                        v-for="option in responseTypes"
                        :key="option.value"
                        :value="option.value"
                      >
                        <a-tooltip :title="option.tooltip">
                          <span style="width: 100%">{{ option.label }}</span>
                        </a-tooltip>
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="24" v-if="formState.data_type && formState.data_type != ResponseType.String">
                  <a-form-item name="data_value">
                    <template
                      v-if="formState.created_at && formState.created_at == formState.updated_at"
                      #extra
                    >
                      Por defecto todos estos valores tendrán la acción de “Finalizar llamada” desde la configuración del IVR Operador Automático y/o Chatbot de WhatsApp. 
                      Recuerda que puedes editarlo según lo requieras.
                    </template>
                    <template #label>
                      <span v-if="formState.data_type == ResponseType.Number">
                        Valores del tipo numérico
                      </span>
                      <span v-else>
                        Valores del tipo booleano
                      </span>
                      <a-tooltip>
                        <template #title>
                          <span v-if="formState.data_type == ResponseType.Number">
                            Define qué representa cada valor que devuelve la API. Esto permite decidir qué audio reproducir o qué acción ejecutar.
                          </span>
                          <span v-else>
                            Devuelve Verdadero (true) o Falso (false), el sistema solo necesita saber si la condición se cumple o no.
                          </span>
                        </template>
                        <InfoCircleOutlined style="margin-left: 5px" />
                      </a-tooltip>
                    </template>
                    <a-input
                      v-if="formState.data_type == ResponseType.Number"
                      v-model:value="formState.data_value"
                      @input="onNumberCommaInput"
                    />
                    <a-input v-else placeholder="Verdadero/falso" disabled />
                  </a-form-item>
                </a-col>
              </a-row>
            </a-card>
          </div>
        </a-form>
      </div>
    </div>
  </CustomScrollbar>
  
</template>
<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { connectorServices } from "@/app/ivr/bot/services/ConnectorServices";
import { ArrowLeftOutlined, InfoCircleOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import { Connector } from "@/@types/ivr";
import { useRoute, useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import CustomScrollbar from "@/app/shared/components/organisms/CustomScrollbar.vue";
//import PropertiesCard from "../cards/PropertiesCard.vue";
import { AuthenticationType, requestTypes, authenticationTypes, sendTypes, responseTypes, RequestType, DescriptionAuthenticationType, ImageAuthenticationType, ResponseType, DescriptionRequestType } from "@/app/ivr/bot/utils/enums";
import { createVNode } from "vue";
import StandardPropertiesCard from "../cards/StandardPropertiesCard.vue";
import imageResponseApi from "@/app/ivr/assets/response_api.png";
import { cloneDeep, debounce, isEqual } from "lodash";
import { error } from "console";

const props = withDefaults(
  defineProps<{
    isEdit?: boolean;
  }>(),
  {
    isEdit: false,
  }
);

const formState = ref<Connector>({
  id: 0,
  created_at: null,
  updated_at: null,
  deleted_at: null,
  status: 1,
  name: "",
  description: "",
  type_request: RequestType.Get,
  url: "",
  type_authentication: null,
  username: null,
  password: null,
  api_key: null,
  url_token: null,
  username_token: null,
  password_token: null,
  ivr_param_name: "",
  standard_properties: [],
  api_return_field: "",
  data_type: null,
  data_value: "",
  type_send: null,
});

const formStateCompare = ref<Connector | null>(null);

const router = useRouter();
const route = useRoute();
const formRef = ref();
const loading = ref(false);
const loadingFetch = ref(false);
const itemConnector = Number(route.params?.connectorId) || null;
const disabledSave = ref(true);

const rules = {
  name: [
    { required: true, message: "Campo requerido" },
    {
      validator: (_: any, value: string) =>
        new Promise((resolve, reject) => {

          if (isSameName.value) return resolve(true);

          debouncedValidator(value, (msg: string) => {
            msg ? reject(msg) : resolve(true);
          });

        }),
      trigger: ["change", "blur"]
    }
  ],
  description: [{ required: true, message: 'Campo requerido' }],
  type_request: [{ required: true, message: 'Campo requerido' }],
  url: [
    { required: true, message: "Campo requerido" },
    { 
      pattern: /^(https?:\/\/|www\.)/i, 
      message: "La URL debe empezar con https://, http:// o www"
    },
  ],
  type_authentication: [{ required: true, message: 'Campo requerido' }],
  username: [{ required: true, message: 'Campo requerido' }],
  password: [{ required: true, message: "Campo requerido" }],
  api_key: [{ required: true, message: 'Campo requerido' }],
  url_token: [{ required: true, message: 'Campo requerido' }],
  username_token: [{ required: true, message: "Campo requerido" }],
  password_token: [{ required: true, message: 'Campo requerido' }],
  ivr_param_name: [{ required: true, message: 'Campo requerido' }],
  api_return_field: [{ required: true, message: "Campo requerido" }],
  data_type: [{ required: true, message: 'Campo requerido' }],
  data_value: [{ required: true, message: 'Campo requerido' }],
};

const showTooltipOption = ref(false);

const hasChanges = computed(() => {
  return !isEqual(formState.value, formStateCompare.value);

  // Si está editando, comparo con el estado original
  // if (props.isEdit) {
  //   return !isEqual(formState.value, formStateCompare.value);
  // }

  // Si es nuevo, verifico si llenaron al menos un campo
  // const fields = Object.values(formState.value)
  //   .filter((v) => v !== null && v !== "" && v !== 0 && !(Array.isArray(v) && v.length === 0));

  // return fields.length > 0;
});

const isSameName = computed(() => {
  if (!props.isEdit || !formStateCompare.value) return false;
  return formState.value.name === formStateCompare.value.name;
});

watch(
  () => formState.value,
  async () => {
    try {
      if (props.isEdit) {
        if (isEqual(formState.value, formStateCompare.value)) {
          disabledSave.value = true;
          return;
        }
      }
      await formRef.value?.validate()
      disabledSave.value = false;
    } catch (error) {
      console.log(error, 'error')
      disabledSave.value = true;
    }
  },
  { deep: true }
);

const checkNameExists = async (name: string) => {
  if (!name || !name.trim()) return false;

  try {
    const result = await connectorServices.validateName(name);
    return result.exists;
  } catch (e) {
    console.error("Error verificando nombre:", e);
    return false;
  }
};

const loadingName = ref(false);

const debouncedValidator = debounce(async (name, callback) => {
  loadingName.value = true;
  try {
    const exists = await checkNameExists(name);
    if (exists) {
      callback("Ya existe un conector con este nombre");
    } else {
      callback();
    }
  } catch(error) {
    console.error(error)
  } finally {
    loadingName.value = false;
  }
}, 400);

const clearFieldsAuthentication = () => {
  formState.value.username = null
  formState.value.password = null
  formState.value.api_key = null
  formState.value.url_token = null
  formState.value.username_token = null
  formState.value.password_token = null
}

const returnRoute = () => {
  if (hasChanges.value) {
    Modal.confirm({
      title: "¿Deseas descartar tu progreso realizado?",
      content: "Al descartar, no se quedarán guardados los datos previamente completados. ",
      okText: "Sí, descartar",
      cancelText: "No, seguir completando",
      centered: true,
      onOk() {
        router.push(`/flows/utility?key=connector`);
      }
    });
  } else {
    router.push(`/flows/utility?key=connector`);
  }
};

const handleSubmit = async () => {
  await formRef.value.validateFields();
  const raw = formState.value.data_value;

  let data_value: any[] = []
  if (Array.isArray(raw)) {
    data_value = raw;
  } else if (typeof raw === "string") {
    data_value = raw.split(",");
  }

  data_value = data_value
    .map((val: string) => {
      const trimmed = typeof val == "string" ? val.trim() : val;

      if (["true", "false"].includes(trimmed))
        return trimmed;

      // Convertir a número si es válido
      const numberValue = Number(trimmed);
      if (!isNaN(numberValue)) return numberValue;

      // Si no es boolean ni número, devolver el string original
      return trimmed;
    });
    
  if(props.isEdit && itemConnector) {
    const ivrs = formState.value?.ivr || []
    if (ivrs.length > 0 && 
      (formState.value?.data_type != formStateCompare.value?.data_type ||
      !isEqual(formState.value?.data_value, formStateCompare.value?.data_value))
    ) {
      Modal.confirm({
        title: "Estás a punto de guardar cambios en tu conector de API, ¿deseas continuar?",
        content: "Al guardar los cambios se perderán las configuraciones en todos los IVR operadores automáticos y/o chatbots de WhatsApp vinculados. Asegúrate de revisar luego la configuración de cada uno antes de activarlos.",
        icon: createVNode(QuestionCircleOutlined),
        centered: true,
        okText: "Sí, guardar cambios",
        cancelText: "No, seguir editando",
        async onOk() {
          await handleEdit(data_value);
        },
      });
    } else
      await handleEdit(data_value);
  }else {
    await handleSave(data_value);
  }
};

const handleSave = async (data_value: any[]) => {
  try{
    loading.value = true;
    const body: any = { ...formState.value, data_value: data_value }
    await connectorServices.create(body);
    router.push(`/flows/utility?key=connector`);       
    message.success("Conector de API integrado con éxito");
  } catch (error) {
    const errorMessage = error?.getErrorMessage?.() || "Ocurrió un error al integrar Conector de API";
    message.error(errorMessage);
    console.error('Error onCreate:', error);
  } finally {
    loading.value = false;
  }
}

const handleEdit = async (data_value: any[]) => {
  try {
    loading.value = true;
    const body: any = { ...formState.value, data_value: data_value }
    await connectorServices.update(itemConnector, body);
    router.push(`/flows/utility?key=connector`);       
    message.success("Cambios realizados con éxito");
  } catch (error) {
    const errorMessage = error?.getErrorMessage?.() || "Ocurrió un error al editar Conector de API";
    message.error(errorMessage);
    console.error('Error onSave:', error);
  } finally {
    loading.value = false;
  }
}

const changeResponseType = (value: number) => {
  console.log(value, 'value---')
  if (value == ResponseType.Number)
    formState.value.data_value = null
  else if (value == ResponseType.Boolean)
    formState.value.data_value = "true,false"
  else
    formState.value.data_value = ""
}

const onNumberCommaInput = (e: any) => {
  let value = e.target.value;
  // Solo números y comas
  value = value.replace(/[^0-9,]/g, "");

  // Evita comas consecutivas o coma al inicio
  value = value.replace(/,,+/g, ",").replace(/^,/, "");

  formState.value.data_value = value;
};

onMounted(async () => {
  try {
    if (props.isEdit && itemConnector) {
      loadingFetch.value = true;
      const connector = await connectorServices.getDetails(itemConnector);
      Object.assign(formState.value, connector);
      formStateCompare.value = cloneDeep(connector);
      // formState.value = { ...formState.value, ...connector};
    } else {
      formStateCompare.value = cloneDeep(formState.value);
    }
  } catch (error) {
    console.error('Error onMounted:', error);
    message.error('No se pudo cargar el conector')
  } finally {
    loadingFetch.value = false
  }
});
</script>

<style lang="sass" scoped>
.card-header
  display: flex
  justify-content: space-between
  align-items: center
  margin-bottom: 10px
  button
    padding: 0
.header
  padding: 8px 0
  display: flex
  width: 100%
  justify-content: space-between
  align-items: center
  // border-bottom: 1px solid $gray-5
  &_route
    margin-left: 5px
    .item
      font-weight: 500
      cursor: pointer
    .subitem
      color: #A4ADB7
.form_container
  height: calc(100vh - 111px)
  // display: grid
  width: 780px
  // grid-template-columns: repeat(auto-fit, minmax(780px, 1fr))
  align-items: start 
  border: none
  padding: 0 30px
  .section_header
    display: flex
    justify-content: space-between
    &--detail
      color: $gray-7
      font-style: italic
  .title
    font-size: 16px
    margin-bottom: 10px
    margin-top: 20px
  .subtitle
    margin-bottom: 10px
    &--extra
      font-weight: 400
      color: $gray-7
  .section
    margin-top: 20px
.container
  margin: min(30px, 20%) auto 0
  width: fit-content
  h3
    margin-top: 0
    font-weight: 600
  &_item
    padding: 8px 15px
    :deep(.ps__rail-x)
      display: none
    &--title
      display: flex
      justify-content: space-between
      align-items: center
      button
        margin-left: 5px
    .ant-form
      padding: 10px 30px
    &--chat
      padding: 0
      height: calc(100% - 40px)
  &_item:nth-child(n+1):not(:nth-child(2))
    border-top: 1px solid #A4ADB7
    border-bottom: 1px solid #A4ADB7
// .container:first-child
//   border-right: 1px solid #A4ADB7
.paragraph
  display: flex
  justify-content: space-between
  margin: 0 0 8px
  color: #8c8c8c
.label-container 
  display: flex
  justify-content: space-between
  align-items: center
  width: 100%

  .expand-icon 
    font-size: 16px
    cursor: pointer
    color: gray
    transition: color 0.3s ease

    &:hover 
      color: black

.property_title
  display: flex
  align-items: center
  gap: 5px
:deep(.ant-form-item-label > label)
  width: 100%

:deep(.ant-upload-list-item .ant-upload-list-item-action)
  opacity: 1 !important
  visibility: visible !important
  color: red

:deep(.ant-upload-list-item .ant-upload-list-item-actions span)
  color: red !important
.description-modal
  margin-bottom: 27px
:global(.textarea-modal .ant-modal-content .ant-modal-close)
  margin-right: 100px !important

:global(.empty-card .ant-card-body)
  padding: 0
  background: $gray-2
  color: #00000073

:global(.custom-pop)
  .ant-popover-inner
    width: 475px
</style>